import { useEffect, useMemo, useRef } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import { BdmResponse, RegionResponse } from '@pmp/api';

export interface BDMValue {
  regionId?: string | null;
  regionName?: string | null;
  bdmId?: string | null;
  bdmName?: string | null;
}

export interface BDMFieldProps {
  value?: BDMValue;
  onChange?: (value: BDMValue) => void;
  readonly?: boolean;
  regions: RegionResponse[];
  bdms: BdmResponse[];
  loading?: boolean;
  /* Pre-select the region (and its BDM when unambiguous) for new records when the lender has exactly one region */
  autoSelectSingleRegion?: boolean;
}

export const BDMField: React.FC<BDMFieldProps> = ({
  value = {},
  onChange,
  readonly = false,
  regions,
  bdms,
  loading = false,
  autoSelectSingleRegion = false,
}) => {
  const { t } = useTranslation();
  const { regionId, bdmId, bdmName } = value;

  const regionOptions = useMemo(() => regions.map(r => ({ label: r.name, value: r.id })), [regions]);
  const selectedBdm = useMemo(() => (bdmId ? (bdms.find(b => b.id === bdmId) ?? null) : null), [bdmId, bdms]);
  const effectiveRegionId = regionId ?? selectedBdm?.regionIds?.[0] ?? null;
  const bdmsInRegion = useMemo(
    () => (effectiveRegionId ? bdms.filter(b => b.regionIds.includes(effectiveRegionId)) : []),
    [bdms, effectiveRegionId],
  );
  const bdmOptions = useMemo(() => bdmsInRegion.map(b => ({ label: b.name, value: b.id })), [bdmsInRegion]);

  const autoSelectedRef = useRef(false);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!autoSelectSingleRegion || autoSelectedRef.current || readonly) return;
    if (regionId || bdmId || bdmName) return;
    if (regions.length !== 1) return;
    autoSelectedRef.current = true;
    const onlyRegion = regions[0];
    const inRegion = bdms.filter(b => b.regionIds.includes(onlyRegion.id));
    const auto = inRegion.length === 1 ? inRegion[0] : null;
    onChange?.({ regionId: onlyRegion.id, regionName: onlyRegion.name, bdmId: auto?.id ?? null, bdmName: auto?.name ?? null });
  }, [autoSelectSingleRegion, readonly, regionId, bdmId, bdmName, regions, bdms, onChange]);

  useEffect(() => {
    if (resolvedRef.current || bdmId || !bdmName || bdms.length === 0) return;
    const target = bdmName.trim().toLowerCase();
    const match = bdms.find(b => b.name?.trim().toLowerCase() === target);
    if (!match) return;
    resolvedRef.current = true;
    const resolvedRegionId = regionId ?? (match.regionIds.length === 1 ? match.regionIds[0] : null);
    onChange?.({
      regionId: resolvedRegionId,
      regionName: resolvedRegionId ? (regions.find(r => r.id === resolvedRegionId)?.name ?? null) : null,
      bdmId: match.id,
      bdmName: match.name,
    });
  }, [bdmId, bdmName, bdms, regions, regionId, onChange]);

  const onRegionChange = (newRegionId: string | undefined) => {
    if (!newRegionId) {
      onChange?.({ regionId: null, regionName: null, bdmId: null, bdmName: null });
      return;
    }
    const inRegion = bdms.filter(b => b.regionIds.includes(newRegionId));
    const auto = inRegion.length === 1 ? inRegion[0] : null;
    onChange?.({
      regionId: newRegionId,
      regionName: regions.find(r => r.id === newRegionId)?.name ?? null,
      bdmId: auto?.id ?? null,
      bdmName: auto?.name ?? null,
    });
  };

  const onBdmChange = (newBdmId: string | undefined) => {
    const bdm = newBdmId ? bdms.find(b => b.id === newBdmId) : null;
    onChange?.({ ...value, bdmId: bdm?.id ?? null, bdmName: bdm?.name ?? null });
  };

  return (
    <Row gutter={16}>
      <Col span={8}>
        <Form.Item label={t('firms.labels.region')}>
          <Select
            placeholder={readonly ? undefined : t('firms.labels.regionPlaceholder')}
            options={regionOptions}
            loading={loading}
            allowClear
            showSearch
            optionFilterProp="label"
            value={effectiveRegionId ?? undefined}
            onChange={onRegionChange}
            disabled={readonly}
          />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item label={t('firms.labels.businessDevelopmentManager')}>
          <Select
            placeholder={
              readonly
                ? undefined
                : effectiveRegionId
                  ? t('firms.labels.businessDevelopmentManagerPlaceholder')
                  : t('firms.labels.pickARegionPlaceholder')
            }
            options={bdmOptions}
            loading={loading}
            allowClear
            showSearch
            optionFilterProp="label"
            // Fall back to the name when the selected BDM is not in the options (legacy record, region not set) — never show the raw id
            value={bdmId && bdmOptions.some(o => o.value === bdmId) ? bdmId : (selectedBdm?.name ?? bdmName ?? undefined)}
            onChange={onBdmChange}
            disabled={readonly || !effectiveRegionId}
          />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item label={t('firms.labels.bdmContactNumber')}>
          <Input value={selectedBdm?.contactNumber ?? ''} readOnly disabled />
        </Form.Item>
      </Col>
    </Row>
  );
};
